// HUD: paski (HP, XP, głód, pragnienie), nazwa ulicy, minimapa, przyciski akcji, czat, stan sieci, ekran śmierci.
import type { Chunk } from '../world/chunkCodec.js';
import type { Grid } from '../world/coords.js';
import { B, R } from '../world/tiles.js';
import { GROUND, MINIMAP_ROAD, WALL_TOP } from '../render/palette.js';
import { h, ui } from './dom.js';

export type HudAction = 'attack' | 'heal' | 'inventory' | 'quests' | 'chat' | 'menu' | 'map';

function bar(cls: string, label: string) {
  const fill = h('i');
  const text = h('em');
  const el = h('div', { class: `bar ${cls}`, title: label }, fill, text);
  return { el, set(v: number, max: number, txt?: string) { fill.style.width = `${Math.max(0, Math.min(100, (v / max) * 100))}%`; text.textContent = txt ?? `${Math.round(v)}/${max}`; } };
}

export class Hud {
  hp = bar('hp', 'Zdrowie');
  xp = bar('xp', 'Doświadczenie');
  hunger = bar('hunger', 'Głód');
  thirst = bar('thirst', 'Pragnienie');
  private street = h('div', { class: 'street' });
  private net = h('div', { class: 'net' });
  private level = h('div', { class: 'level' }, '1');
  private chatLog = h('div', { class: 'chat-log' });
  private chatInput = h('input', { class: 'chat-input', placeholder: 'Napisz…', maxlength: '280', enterkeyhint: 'send' });
  private chatBox = h('div', { class: 'chat' }, this.chatLog, this.chatInput);
  private minimap = h('canvas', { class: 'minimap', width: '160', height: '160' });
  private death = h('div', { class: 'death' });
  private target = h('div', { class: 'target' });
  onAction: (a: HudAction) => void = () => {};
  onChat: (text: string) => void = () => {};

  constructor() {
    const mapWrap = h('div', { class: 'minimap-wrap', title: 'Mapa świata' }, this.minimap);
    mapWrap.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.onAction('map'); });
    const top = h('div', { class: 'hud-top' },
      h('div', { class: 'vitals' }, h('div', { class: 'row' }, this.level, this.hp.el), this.xp.el,
        h('div', { class: 'row small' }, this.hunger.el, this.thirst.el)),
      this.street, h('div', { class: 'top-right' }, this.net, mapWrap));
    const btn = (a: HudAction, label: string, cls = '') => {
      const b = h('button', { class: `act ${cls}`, 'aria-label': label }, label);
      b.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.onAction(a); });
      return b;
    };
    const actions = h('div', { class: 'actions' }, btn('attack', 'Atak', 'big'), btn('heal', 'Lecz'),
      btn('inventory', 'Plecak'), btn('quests', 'Zadania'), btn('chat', 'Czat'), btn('menu', '≡'));
    this.chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this.chatInput.value.trim()) { this.onChat(this.chatInput.value.trim()); this.chatInput.value = ''; }
      if (e.key === 'Escape') this.toggleChat(false);
    });
    ui().append(top, actions, this.chatBox, this.death, this.target);
  }

  setLevel(l: number): void { this.level.textContent = String(l); }
  setStreet(street: string | null, area: string | null): void {
    this.street.textContent = [street, area].filter(Boolean).join(' · ') || '';
  }
  setNet(status: string, rtt = 0): void {
    this.net.className = `net ${status}`;
    this.net.textContent = status === 'online' ? `${Math.round(rtt)} ms` : status === 'connecting' ? 'łączenie…' : 'offline';
  }
  setTarget(name: string | null, hpPct = 0): void {
    this.target.style.display = name ? '' : 'none';
    if (name) this.target.innerHTML = `<b></b><span><i style="width:${hpPct}%"></i></span>`, (this.target.querySelector('b')!.textContent = name);
  }
  toggleChat(force?: boolean): void {
    const open = force ?? !this.chatBox.classList.contains('open');
    this.chatBox.classList.toggle('open', open);
    if (open) this.chatInput.focus(); else this.chatInput.blur();
  }
  chat(from: string, msg: string, system = false): void {
    const line = h('div', { class: system ? 'sys' : '' });
    if (!system) line.append(h('b', {}, `${from}: `));
    line.append(msg);
    this.chatLog.append(line);
    while (this.chatLog.children.length > 60) this.chatLog.firstChild?.remove();
    this.chatLog.scrollTop = this.chatLog.scrollHeight;
  }
  showDeath(by: string, onRespawn: () => void): void {
    this.death.replaceChildren(h('h1', {}, 'NIE ŻYJESZ'), h('p', {}, `Zabił Cię: ${by}`));
    this.death.classList.add('on');
    let n = 5;
    const p = h('p', { class: 'count' }, `Odrodzenie za ${n}…`);
    this.death.append(p);
    const t = setInterval(() => {
      n -= 1; p.textContent = `Odrodzenie za ${n}…`;
      if (n <= 0) { clearInterval(t); this.death.classList.remove('on'); onRespawn(); }
    }, 1000);
  }

  /** Minimapa z danych chunków (widok z góry, obrócony CSS-em o 45° do zgodności z izometrią). */
  drawMinimap(chunks: Iterable<Chunk>, grid: Grid, px: number, py: number, others: [number, number, string][]): void {
    const ctx = this.minimap.getContext('2d')!;
    const S = 160, scale = 0.5;           // 0,5 px na metr → 320 m szerokości
    ctx.fillStyle = '#050606'; ctx.fillRect(0, 0, S, S);
    const n = grid.chunkTiles;
    for (const c of chunks) {
      const ox = (c.cx * n * grid.tile - px) * scale + S / 2, oy = (c.cy * n * grid.tile - py) * scale + S / 2;
      if (ox > S || oy > S || ox + n * scale < 0 || oy + n * scale < 0) continue;
      for (let ly = 0; ly < n; ly += 2) {
        for (let lx = 0; lx < n; lx += 2) {
          const i = ly * n + lx;
          ctx.fillStyle = c.building[i] === B.WALL || c.building[i] === B.FLOOR ? WALL_TOP
            : c.road[i] !== R.NONE && c.road[i] !== R.SIDEWALK ? MINIMAP_ROAD : GROUND[c.ground[i]] ?? '#222';
          ctx.fillRect(ox + lx * scale, oy + ly * scale, 1, 1);
        }
      }
    }
    for (const [x, y, kind] of others) {
      ctx.fillStyle = kind === 'z' ? '#c2412d' : '#5aa0d8';
      ctx.fillRect((x - px) * scale + S / 2 - 1, (y - py) * scale + S / 2 - 1, 3, 3);
    }
    ctx.fillStyle = '#f0c040'; ctx.fillRect(S / 2 - 2, S / 2 - 2, 4, 4);
  }
}
